import CryptoJS from "crypto-js";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppleSvg from "../../../assets/images/Apple.svg";
import GoogleSvg from "../../../assets/images/Google.svg";
import health360Icon from "../../../assets/images/health360.svg";
import DownloadBtn from "../../../components/common/downloadBtn/downloadBtn";
import BackdropModal from "../../../components/common/modal/backdropModal";
import MobileFooter from "../../../components/common/sideNavbar/mobileFooter";
import Tabs from "../../../components/common/tabs/tabs";
import NonMotorPolicyCard from "../../../components/nonMotorPolicyCard/nonMotorPolicyCard";
import NonMotorPolicyDetailsSkeleton from "../../../components/common/Loader/NonMotorPolicyDetailsSkeleton";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useScreenResolution from "../../../hooks/useScreenResolution";
import { policyView, clearPolicyDetails } from "../../../redux/features/policyDetailsSlice";
import { clearNewMotorFormData } from "../../../redux/features/newMotorFormSlice";
import { decodeQueryString } from "../../../utils";
import { getClientSource } from "../../../utils/getClientSource";
import { policyOverview } from "../../motorInsurancePages/policyOverview/motorPolicyDataFile";
import Breadcrumb from "../../lifeInsurancePages/PolicyOverview/Breadcrumb";

const AmanPolicyDetailsPage = () => {
  const location = useLocation();
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const { policy_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screenResolution = useScreenResolution();
  const isRTL = i18n.resolvedLanguage === "ar";
  const GetPolicyDetails = location.state?.data;

  const secretKey = "i-hate-meej";

  const decryptPolicyId = (encryptedId) => {
    try {
      if (!encryptedId) return "";
      const base64EncryptedValue = encryptedId
        .split("-")
        .join("+")
        .split("_")
        .join("/");
      const bytes = CryptoJS.AES.decrypt(base64EncryptedValue, secretKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return decrypted || "";
    } catch (error) {
      console.error("Error decrypting policy ID:", error);
      return "";
    }
  };

  const PolicyNumber = useMemo(() => decryptPolicyId(policy_id), [policy_id]);
  const Channel = useMemo(() => getClientSource(), []);

  const policyDetails = useSelector(
    (state) => state.policyDetails.PolicyDetails
  );
  const policyDetailsStatus = useSelector(
    (state) => state.policyDetails.status
  );
  const [breadCrumbData, setBreadCrumbData] = useState(null);
  const [docLabel, setDocLabel] = useState([]);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "PolicyOverview"
  );

  const polReq = useMemo(
    () => ({
      PolicyNumber,
      Channel,
    }),
    [PolicyNumber, Channel]
  );

  const handleTabClick = (tabType) => {
    setActiveTab(tabType);
    const Path = `${location.pathname}${location.search}`;
    if (location.state?.activeTab) {
      const { activeTab: tab, ...restState } = location.state;
      navigate(Path, { state: { ...restState, activeTab: "" } });
    }
  };

  useEffect(() => {
    setBreadCrumbData([
      {
        label: translate("My Policy", "وثيقتي"),
        link: "/my-policy",
        clickable: true,
        name: "my_policy",
      },
      {
        label: translate("Aman Policy", "وثيقة أمان"),
        clickable: false,
        name: "current_plan",
        isActive: true,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    if (!PolicyNumber) return;
    
    if (policyDetails?.PolicyHeader?.PolicyNumber !== PolicyNumber) {
      dispatch(clearPolicyDetails());
    }
    
    dispatch(policyView(polReq));
    dispatch(clearNewMotorFormData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, PolicyNumber, Channel, i18n.resolvedLanguage]);

  useEffect(() => {
    if (Array.isArray(policyDetails?.Reports)) {
      setDocLabel(policyDetails.Reports);
    }
  }, [policyDetails?.Reports]);

  const formattedDocLabel = useMemo(() => {
    return docLabel.map((doc) => ({
      ...doc,
      fileName: doc.Name || doc.ID,
      fileName_ar: doc.Name_Ar || doc.Name || doc.ID,
      filesData: {
        url: doc.File,
        size: "",
      },
    }));
  }, [docLabel]);

  const params = decodeQueryString();

  useEffect(() => {
    if (params.tabType) {
      setActiveTab(params.tabType);
    }
  }, [params?.tabType]);

  const Info = {
    PolicyNumber: PolicyNumber,
    status: "",
    Make: "",
    Model: "",
    PlanName: "",
    StartDate: "",
    EndDate: "",
    RegNo: "",
  };

  const tabs = useMemo(
    () => [
      {
        label_en: "Policy Overview",
        label_ar: "نظرة عامة على الوثيقة",
        type: "PolicyOverview",
      },
    ],
    []
  );

  const planName =
    policyDetails?.PolicyHeader?.PlanName ||
    policyDetails?.PolicyHeader?.ProductCode;
  const isAmanPlus =
    planName?.toLowerCase()?.includes("plus") ||
    policyDetails?.PolicyHeader?.ProductCode === "0914";

  const amanBenefits = [
    {
      en: "In-patient and day-care treatments are covered, subject to a three-month waiting period for surgical procedures.",
      ar: "يشمل التأمين العلاج الداخلي والرعاية اليومية، مع فترة انتظار قدرها ثلاثة أشهر للعمليات الجراحية.",
    },
    {
      en: "Outpatient treatments are covered up to BHD 500 per person per year.",
      ar: "يشمل العلاج في العيادات الخارجية بحد أقصى 500 دينار بحريني للشخص في السنة.",
    },
    {
      en: "Consultations are covered for a general practitioner, and for specialists or consultants upon GP referral.",
      ar: "تغطي الاستشارة عند الطبيب العام، كما تُغطى الاستشارة عند الأخصائي أو الاستشاري عند تحويل الحالة من قبل الطبيب العام.",
    },
    {
      en: "Diagnostic procedures including X-ray and laboratory tests are covered subject to approval.",
      ar: "تشمل التغطية الفحوصات التشخيصية مثل الأشعة والتحاليل المخبرية، وذلك بعد الحصول على الموافقة المسبقة.",
    },
    {
      en: "Pharmacy benefits cover generic medications only, following a valid prescription by the treating doctor.",
      ar: "تشمل التغطية الأدوية المكافئة فقط بناءً على وصفة طبية من الطبيب المعالج.",
    },
    {
      en: "A deductible of BHD 5 applies per consultation.",
      ar: "يتحمل المؤمن عليه مبلغ 5 دنانير بحرينية عن كل استشارة.",
    },
    {
      en: "Pre-existing and chronic conditions are not covered.",
      ar: "لا تشمل الوثيقة الحالات السابقة والأمراض المزمنة.",
    },
    {
      en: "Dental treatment, including consultation, amalgam filling, simple extraction and medications, is covered up to BHD 60 per person per year with 20 percent coinsurance.",
      ar: "يشمل علاج الأسنان الاستشارات، والحشوات المعدنية، وخلع الأسنان البسيط، والأدوية بحد أقصى 60 دينار بحريني للشخص في السنة مع نسبة مشاركة تأمينية قدرها 20٪.",
    },
    {
      en: "Maternity treatment, limited to nine prenatal visits and one postnatal visit, is covered up to BHD 100 per member per year with 20 percent coinsurance.",
      ar: "يشمل علاج الحمل تسع زيارات متابعة أثناء الحمل وزيارة واحدة بعد الولادة، بحد أقصى 100 دينار بحريني للشخص في السنة مع نسبة مشاركة تأمينية قدرها 20٪.",
    },
    {
      en: "The annual maximum limit is BHD 2,000 per person per year.",
      ar: "الحد الأقصى السنوي للتغطية هو 2000 دينار بحريني للشخص في السنة.",
    },
    {
      en: "Coverage is limited to Bahrain.",
      ar: "التغطية الجغرافية داخل مملكة البحرين فقط.",
    },
    {
      en: "Services must be obtained within the approved provider network.",
      ar: "يجب الحصول على الخدمات من ضمن شبكة مقدمي الخدمة المعتمدين.",
    },
  ];

  if (isAmanPlus) {
    amanBenefits.push({
      en: "Death of domestic helper coverage included.",
      ar: "تغطية وفاة العامل المنزلي مشمولة.",
    });
  }

  if (!PolicyNumber) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[50vh]">
        <p className="text-[#1A2024] text-lg font-medium mb-4">
          {translate("Policy not found", "الوثيقة غير موجودة")}
        </p>
        <button
          onClick={() => navigate("/my-policy")}
          className="bg-[#55C6EF] text-[#1A2024] px-6 py-3 rounded-[10px] font-semibold"
        >
          {translate("Back to My Policies", "العودة إلى وثائقي")}
        </button>
      </div>
    );
  }

  if (
    policyDetailsStatus === "loading" ||
    !policyDetails?.PolicyHeader ||
    policyDetails?.PolicyHeader?.PolicyNumber !== PolicyNumber
  ) {
    return <NonMotorPolicyDetailsSkeleton partialData={GetPolicyDetails} />;
  }

  const renderMobilePolicyOverview = () => (
    <div className="flex flex-col gap-6 px-5 pb-32">
      <div className="bg-[#F6F8F9] rounded-[10px] p-6">
        <p
          className={`text-[#1A2024] text-sm font-medium mb-4 ${
            isRTL ? "text-right" : ""
          }`}
        >
          {translate(
            "Download Health 360 to get your eCard",
            "قم بتنزيل Health 360 للحصول على بطاقة الهوية الإلكترونية الخاصة بك"
          )}
        </p>
        <div className="flex items-center gap-4" dir={isRTL ? "rtl" : "ltr"}>
          <div className="w-16 h-16 rounded-[6px] overflow-hidden flex-shrink-0">
            <img
              src={health360Icon}
              alt="Health 360"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.innerHTML =
                  '<div class="w-full h-full bg-[#55C6EF] flex items-center justify-center text-white font-bold">H360</div>';
              }}
            />
          </div>
          <div className="flex flex-col gap-4">
            <p className="text-[#48535B] text-lg font-medium">Health 360</p>
            <div className="flex gap-2">
              <a 
                href="https://play.google.com/store/apps/details?id=com.health360.app&pcampaignid=web_share" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <img src={GoogleSvg} alt="Google Play" className="h-[30px]" />
              </a>
              <a 
                href="https://apps.apple.com/bh/app/health-360-ancillary-services/id1273593493" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <img src={AppleSvg} alt="App Store" className="h-[30px]" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <p
          className={`text-[#1A2024] text-lg font-medium ${
            isRTL ? "text-right" : ""
          }`}
        >
          {translate("Included Benefits", "المزايا المشمولة")}
        </p>
        <div className="flex flex-col gap-2" dir={isRTL ? "rtl" : "ltr"}>
          {amanBenefits.map((benefit, index) => (
            <div
              key={index}
              className="flex items-start gap-2 w-full"
            >
              <div className="flex-shrink-0 w-6 h-6">
                <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
                  <path
                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                    fill="#47D16C"
                  />
                </svg>
              </div>
              <p className="flex-1 text-black text-sm leading-[1.5]">
                {translate(benefit.en, benefit.ar)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {docLabel.length > 0 && (
        <div className="bg-[#F6F8F9] rounded-[10px] p-6" dir={isRTL ? "rtl" : "ltr"}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[#1A2024] text-sm font-medium">
              {translate(
                "Your insurance policy documents:",
                "مستندات بوليصة التأمین الخاصة بك:"
              )}
            </p>
          </div>
          <DownloadBtn filesData={formattedDocLabel} className={"gap-2 shrink w-full"} />
        </div>
      )}
    </div>
  );

  const renderDesktopPolicyOverview = () => (
    <div className="flex gap-4">
      <div className="flex-[2] bg-white border border-[#DDE2E4] rounded-[10px] p-6">
        <h3
          className={`text-[#1A2024] text-lg font-medium mb-4 ${
            isRTL ? "text-right" : ""
          }`}
        >
          {translate("Included Benefits", "المزايا المشمولة")}
        </h3>
        <div className="space-y-3" dir={isRTL ? "rtl" : "ltr"}>
          {amanBenefits.map((benefit, index) => (
            <div
              key={index}
              className="flex items-start gap-2 w-full"
            >
              <div className="flex-shrink-0 w-6 h-6">
                <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
                  <path
                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                    fill="#47D16C"
                  />
                </svg>
              </div>
              <span className="flex-1 text-black text-sm leading-[1.5]">
                {translate(benefit.en, benefit.ar)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 space-y-4">
        <div className="bg-[#F6F8F9] rounded-[10px] p-6">
          <p
            className={`text-[#1A2024] text-sm font-medium mb-4 ${
              isRTL ? "text-right" : ""
            }`}
          >
            {translate(
              "Download Health 360 to get your eCard",
              "قم بتنزيل تطبيق Health 360 للحصول على بطاقتك الإلكترونية"
            )}
          </p>
          <div className="flex items-center gap-4" dir={isRTL ? "rtl" : "ltr"}>
            <div className="w-16 h-16 rounded-[6px] overflow-hidden bg-[#55C6EF] flex items-center justify-center flex-shrink-0">
              <img
                src={health360Icon}
                alt="Health 360"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentElement.innerHTML =
                    '<span class="text-white font-bold text-lg">H360</span>';
                }}
              />
            </div>
            <div className="flex flex-col gap-4">
              <p className="text-[#48535B] text-lg font-medium">Health 360</p>
              <div className="flex gap-2">
                <a 
                  href="https://play.google.com/store/apps/details?id=com.health360" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <img src={GoogleSvg} alt="Google Play" className="h-[30px]" />
                </a>
                <a 
                  href="https://apps.apple.com/app/health360" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <img src={AppleSvg} alt="App Store" className="h-[30px]" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#F6F8F9] rounded-[10px] p-6" dir={isRTL ? "rtl" : "ltr"}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[#1A2024] text-sm font-medium">
              {translate(
                "Your insurance policy documents:",
                "مستندات بوليصة التأمين الخاصة بك:"
              )}
            </p>
            {docLabel.length > 2 && (
              <button 
                onClick={() => setShowDocumentsModal(true)}
                className="text-[#00384D] text-sm font-bold underline"
              >
                {translate("View All", "عرض الكل")}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {docLabel.length > 0 ? (
              <DownloadBtn
                filesData={formattedDocLabel.slice(0, 2)}
                className={"gap-2 shrink w-full"}
              />
            ) : (
              <p className="text-[#84919A] text-sm">
                {translate("No documents available", "لا توجد مستندات متاحة")}
              </p>
            )}
          </div>
        </div>

        {/* Documents Modal */}
        <BackdropModal
          show={showDocumentsModal}
          onClose={() => setShowDocumentsModal(false)}
          title={translate("Download Documents", "تنزيل المستندات")}
          contentClassName="!max-w-[560px] !w-[560px]"
          headerClassName="justify-between"
        >
          <div className="gap-2 download-wrapper flex flex-col w-full !max-h-[560px] overflow-y-auto">
            <DownloadBtn
              filesData={formattedDocLabel}
              className="gap-2 shrink w-full mb-2 !max-w-full"
            />
          </div>
        </BackdropModal>
      </div>
    </div>
  );

  if (screenResolution.mobile) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-[#EDFAFF]">
          <NonMotorPolicyCard
            getPolicyDetails={Info}
            content={policyOverview?.policyInfo}
            car={policyDetails?.PolicyHeader}
            isRenewable={false}
          />
        </div>
        <div className="px-5 py-5">
          <div
            className={`border-b border-[#DDE2E4] flex ${
              isRTL ? "justify-end" : "justify-start"
            }`}
          >
            <div className="flex flex-col gap-px h-10 items-center">
              <div className="flex flex-1 items-center">
                <p
                  className={`text-[#00719A] text-lg font-semibold tracking-[-0.18px] ${
                    isRTL ? "font-bold" : ""
                  }`}
                >
                  {translate("Policy Overview", "نظرة عامة على الوثيقة")}
                </p>
              </div>
              <div className="bg-[#00719A] h-px w-full" />
            </div>
          </div>
        </div>
        {activeTab === "PolicyOverview" && renderMobilePolicyOverview()}
        <MobileFooter />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center">
        <Breadcrumb items={breadCrumbData} />
      </div>
      <NonMotorPolicyCard
        getPolicyDetails={Info}
        content={policyOverview?.policyInfo}
        car={policyDetails?.PolicyHeader}
        isRenewable={false}
      />
      <Tabs tabs={tabs} activeTab={activeTab} onTabClick={handleTabClick} />
      {activeTab === "PolicyOverview" && renderDesktopPolicyOverview()}
    </div>
  );
};

export default AmanPolicyDetailsPage;
